import { useMemo } from "react"

import { useAPIHealth } from "shared/api/health/useAPIHealth"

import {
  useProjectAttachmentCategoriesQuery,
  useProjectAttachmentsByShareLinkQuery
} from "entities/attachments/graphql/project-attachments.queries.generated"
import { useInflationSurchargeQuery } from "entities/projects/graphql/inflation-surcharge/queries.generated"
import { usePriceListsQuery } from "entities/projects/graphql/price-lists/queries.generated"
import { getProductPositionTotal } from "entities/projects/lib/getProductPositionTotal"
import { getProductTotal } from "entities/projects/lib/getProductTotal"
import { useProjectPhases } from "entities/projects/model/useProjectPhases"
import { AggregatedProjectData } from "entities/projects/types/Project"
import { readonlyProjectPermissions } from "entities/projects/types/ProjectPermissions"
import { TeamRole } from "entities/team-members/types/TeamRole"

import { useSharedProjectData } from "./useSharedProjectData"
import { useSharedProjectPrice } from "./useSharedProjectPrice"

export function useAggregatedSharedProjectData({
  projectShareID,
  options
}: {
  projectShareID: string
  options?: { includePrice?: boolean; includeAttachments?: boolean }
}): {
  projectData: AggregatedProjectData
} {
  const { includePrice = true, includeAttachments = false } = options || {}
  const { project, isLoading, isLoadingChildren, error } =
    useSharedProjectData(projectShareID)
  const { price: isPriceServiceAvailable } = useAPIHealth()
  const { projectPhases, isLoading: isLoadingProjectPhases } =
    useProjectPhases()
  const {
    data: inflationSurchargeData,
    loading: isLoadingStandardInflationSurcharge
  } = useInflationSurchargeQuery()

  const { price, isLoading: isLoadingPrice } = useSharedProjectPrice({
    shareID: projectShareID,
    skip: !includePrice
  })
  const { data: priceListData, loading: isLoadingPriceLists } =
    usePriceListsQuery()

  const {
    data: attachmentsData,
    loading: isLoadingAttachments,
    error: attachmentsError
  } = useProjectAttachmentsByShareLinkQuery({
    variables: { id: projectShareID },
    skip: !includeAttachments
  })

  const {
    data: attachmentCategoriesData,
    loading: isLoadingAttachmentCategories,
    error: attachmentCategoriesError
  } = useProjectAttachmentCategoriesQuery({ skip: !includeAttachments })

  const productTotal = useMemo(
    () => (project?.children ? getProductTotal(project.children) : undefined),
    [project?.children]
  )

  const productPositionTotal = useMemo(
    () =>
      project?.children ? getProductPositionTotal(project.children) : undefined,
    [project?.children]
  )

  return {
    projectData: {
      project: {
        data: project,
        isLoading,
        isUpdating: isLoadingChildren,
        error: !!error
      },
      price: {
        data: price,
        isLoading: isLoadingPrice,
        error: !isPriceServiceAvailable
      },
      phases: { data: projectPhases, isLoading: isLoadingProjectPhases },
      permissions: { data: readonlyProjectPermissions },
      role: { data: TeamRole.READ },
      priceLists: {
        data: priceListData?.priceLists,
        isLoading: isLoadingPriceLists
      },

      standardInflationSurcharge: {
        data: inflationSurchargeData?.inflationSurcharge.value,
        isLoading: isLoadingStandardInflationSurcharge
      },
      attachments: includeAttachments
        ? {
            data: attachmentsData?.attachments,
            isLoading: isLoadingAttachments,
            isUpdating: !!attachmentsData?.attachments && isLoadingAttachments,
            error: !!attachmentsError
          }
        : null,
      attachmentCategories: includeAttachments
        ? {
            data: attachmentCategoriesData?.projectAttachmentCategories
              .categories,
            isLoading: isLoadingAttachmentCategories,
            isUpdating:
              !!attachmentCategoriesData?.projectAttachmentCategories
                .categories && isLoadingAttachmentCategories,
            error: !!attachmentCategoriesError
          }
        : null,
      meta: { isPending: false, href: "#", productTotal, productPositionTotal }
    }
  }
}
